import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { getMaxPointSize } from '../scene-manager.js';

// Shared-rig contract (see the framing addendum): the only light in this scene
// is the aurora's own emission and the stars. Nothing here is lit by anything.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'aurora',
  family: 'organic',
  energyLevel: 0.20,
  bpmMin: 40,
  bpmMax: 130,
  movement: 0.30,
  danceability: 0.30,
  tonality: 0.92,
  prefers: { dynamics: [0.35, 1.0], percussiveness: [0.0, 0.40] },
  enabled: true
};

// Aurora: curtains of light standing over a dark horizon.
//
// Three things separate a real aurora from a gradient that moves, and every
// decision below exists to protect one of them:
//
//   1. It is STRIATED. The structure is fine vertical rays lined up on the
//      magnetic field, never a smooth wash. This is what the eye reads first.
//   2. Its base is SHARP and its top DISSIPATES. The vertical gradient is
//      violently asymmetric - a hard lower edge, a long exponential tail.
//   3. Its colour changes with ALTITUDE, green low and red or violet high,
//      because those are different emission lines. It is the single most
//      recognisable marker, and the one thing a coloured gradient never has.
//
// And one thing about the shape. A curtain is a sheet, and it MEANDERS
// horizontally. Seen from the side it therefore folds back on itself, and
// where a fold turns edge-on to the viewer the sheet piles up into a bright
// vertical band. That pile-up is not drawn - it happens because the mesh
// genuinely overlaps itself in screen space and the blending is additive, so
// the fold sums with itself. Everything about the geometry below is arranged
// to let that happen for real rather than to fake it (see CAM_Z).
//
// No director.pulse() anywhere. An aurora does not pulse, it undulates.

// --- tiers -------------------------------------------------------------------
// reflect: how many curtains get mirrored into the water. Zero at tier 0, where
// the water is the tinted gradient alone - much cheaper, and at that distance
// it is enough (waterGain lifts it so the surface still reads as water).
const TIER = {
  0: { curtains: 3, segX: 96, segY: 24, stars: 250, reflect: 0, waterGain: 2.6 },
  1: { curtains: 4, segX: 128, segY: 32, stars: 400, reflect: 3, waterGain: 1.0 },
  2: { curtains: 5, segX: 128, segY: 32, stars: 600, reflect: 3, waterGain: 1.0 }
};
const DEFAULT_TIER = 1;

// --- the stage ---------------------------------------------------------------
// The curtains are placed in a real 3D layout - the brief's z of -2 to -14 -
// but projected by hand in the vertex shader rather than through the shared
// camera. Two reasons, both load-bearing:
//
//   the stars have to be FIXED, and scene-manager's Ken Burns drift moves the
//   camera every frame, which would slide the whole sky;
//
//   and the fold pile-up depends on the exact relationship between a curtain's
//   depth and the viewer, so owning the projection means that relationship is
//   stated here in one place instead of being inherited.
//
// CAM_Z is the part that matters. The brief's bend swings a curtain by up to
// +/-3.05 in z; a viewer at the origin would have the nearest curtain (z = -2)
// swing straight through them and out the back. Standing the viewer back at
// +6 puts every curtain between 5 and 23 units away, so the whole bend stays
// in front - and the near/far size ratio is still 2.5x, which is what the
// brief is actually asking the depth spread for.
const CAM_Z = 6.0;
const Z_NEAR = -2.0;
const Z_FAR = -14.0;
const FOV_DEG = 68; // matches scene-manager's camera, so the framing agrees

// Where the curtains stand, in world units. Negative: below the view axis, so
// the bases sit under the frame's midline. A single shared value for every
// curtain on purpose - it puts them all on one ground plane, and a ground
// plane is what makes a distant curtain's base ride HIGHER in the frame than a
// near one's. That is the whole depth cue, and it is free.
const BASE_WORLD_Y = -2.50;
// Generous, and it has to be. Every curtain standing on one plane puts every
// base within a fifth of a frame-height of every other, and five bright bases
// on one line read as a single horizontal bar lying on the horizon rather than
// as five sheets at five depths.
const BASE_JITTER = 0.62;
// The bottom of a curtain is not a line. Its ending height is pushed around by
// the SAME low-frequency curve that produces the meander, on its own seed - so
// the base wanders in step with the folds instead of against them. 0.35 of the
// bend puts about 20% of the frame height between the highest and lowest point
// of the nearest curtain's base (less on the far ones, which is right: they are
// further away). Before this the curtains all ended on one horizontal rule,
// which was most obvious exactly where it mattered, behind the cover.
const BASE_WAVE = 0.35;
// ...and the last fifth of a curtain dissolves rather than stopping. Note what
// this does to the profile: multiplied into exp(-vH/decay), the brightest point
// is no longer the very bottom edge but a band a little above it, which is
// where a real aurora's is. The base still reads as defined - it is a fifth of
// the height against a decay length of a fifth to a half - it just no longer
// ends on a cut.
const BASE_DISSOLVE = 0.20;
// A curtain is roughly as tall as it is far away is wide. Varied per curtain
// so five of them do not top out on one line.
const HEIGHT_WORLD_MIN = 8.5;
const HEIGHT_WORLD_MAX = 11.5;
// Half-width, as a fraction of the frame's own half-width at that depth. Under
// 1.0 for most of them: real curtains are localised bands, not wall-to-wall
// sheets, and the ones that do not span the frame are what stop this reading
// as five stacked backdrops.
const HALF_WIDTH_MIN = 0.55;
const HALF_WIDTH_MAX = 1.20;
const CENTRE_OFFSET = 0.40;

const BRIGHT_MIN = 0.55;
const BRIGHT_MAX = 1.15;

// The exposure budget, and it is the single most important number in this
// file. The curtains are ADDITIVE, so what lands in the framebuffer is the sum
// of every curtain covering that pixel - and they all stand on one ground
// plane, so at the base they all cover it. Left unbudgeted the sum runs past
// 3.0 there: the base clips to white, the green goes with it, and the bloom
// pass (threshold 0.78, and it runs on the finished frame) turns that clipped
// bar into a halo big enough to swallow the black mountains whole. The first
// render did exactly that.
//
// A fragment cannot see the sum it is being added into, so the budget has to
// be set before the fact: divide by how much overlap there actually is. Not by
// the curtain count - they have different widths and offsets and are nowhere
// near fully coincident - hence the 0.6, which is measured rather than
// reasoned: CURTAIN_GAIN was walked until a five-curtain frame peaked just
// under the brief's 0.90 with a single curtain still reading bright.
// Raised from 0.74 when the base gained its 20% dissolve. That fade multiplies
// into exp(-vH/decay) and moves the profile's maximum up off the bottom edge,
// which costs the bright base band about a third of its value - and the base
// band is both the subject and what the water has to reflect. Measured, not
// guessed: 1.02 puts the frame's peak back where it was.
const CURTAIN_GAIN = 1.02;
const OVERLAP_K = 0.60;

// --- reactivity --------------------------------------------------------------
const LOW_SWING = 0.20;   // bands.low  -> overall intensity, +/-20%
const MID_SWING = 0.25;   // bands.mid  -> undulation speed, +/-25%
const HIGH_STRIA_MIN = 0.45; // bands.high -> how hard the rays bite
const HIGH_STRIA_MAX = 1.00;
const HIGH_DRIFT_MIN = 0.35; // ...and how fast they slide sideways
const HIGH_DRIFT_MAX = 1.60;
const DYNAMICS_AMP_MIN = 0.6;
const DYNAMICS_AMP_MAX = 1.3;

// Approach time constants. Everything the bands touch is eased rather than
// assigned: band envelopes are updated at the analysis timeline's own
// resolution, and an aurora that stepped between values on those boundaries
// would flicker at exactly the rate this piece must not.
const TAU_INTENSITY = 0.45;
const TAU_WAVE = 0.9;
const TAU_STRIA = 0.30;

// progress -> how many curtains are up. Two at the edges of a track, all of
// them across the middle, on a sine so the count is a SMOOTH number - each
// curtain fades in over its own threshold (see CURTAIN_FADE) rather than
// appearing. A curtain that popped into existence would be the most visible
// thing in the frame.
const COUNT_MIN = 2;
const CURTAIN_FADE = 0.55;

// Fallback LFO periods, straight from the brief. Deliberately coprime-ish so
// the two never come back into step.
const LFO_INTENSITY_S = 17;
const LFO_SPEED_S = 29;

// --- colour ------------------------------------------------------------------
// The 557.7nm oxygen line, which is what "aurora" means to anyone who has seen
// one. The brief's 55% pull toward it is not a stylistic default: a cover with
// no green in it would otherwise produce a red or blue curtain, and an
// all-red aurora is both physically wrong and unreadable as an aurora.
const GREEN_ANCHOR = 0x3ff0a0;
const GREEN_PULL = 0.55;
// The high line gets a pull of its own, smaller. Without it a green-dominant
// cover hands BOTH ends of the gradient a green, and the altitude change - the
// most recognisable marker of the three - disappears entirely on exactly the
// covers that look most like an aurora already.
const HIGH_ANCHOR = 0xc8409e;
const HIGH_PULL = 0.30;
const FALLBACK_LOW = 0x2fc9a0;
const FALLBACK_HIGH = 0x9a4fd0;
const PALETTE_LERP_MS = 1100;

// Peak luminance, per the brief. Applied to the two curtain colours before
// they are ever written, so the ceiling is a property of the palette rather
// than a clamp in the inner loop.
const LUMA_CAP = 0.90;
const LUMA = { r: 0.2126, g: 0.7152, b: 0.0722 };

// --- sky, stars, horizon -----------------------------------------------------
const SKY_TOP = 0x000206;
const SKY_HORIZON = 0x081522;
const STAR_SIZE_MIN = 0.7;
const STAR_SIZE_MAX = 1.9;
const STAR_FLOOR_NDC = -0.38; // nothing behind the peaks, it would be hidden
// A share of the stars is mirrored into the water. Static positions, computed
// once with everything else - a reflected star costs nothing per frame.
const STAR_REFLECT_FRACTION = 0.30;
const STAR_REFLECT_LEVEL = 0.13;

// --- the horizon, the land and the water -------------------------------------
// WATER_Y is THE horizon: the shoreline, where the land meets the lake. At
// -0.62 it sits 81% of the way down the frame, which is what an aurora
// photograph actually looks like - the sky is the subject and it takes four
// fifths of the picture.
const WATER_Y = -0.62;
// Ridge height ABOVE the waterline. Sharpened by RIDGE_SHARPEN below, which
// pushes most of the profile down toward the minimum and leaves the peaks
// standing: the mean silhouette lands near 77% and the tallest peaks reach
// about 70%, which is a skyline rather than the soft swell it replaces.
const RIDGE_H_MIN = 0.02;
const RIDGE_H_MAX = 0.21;
// 7 is steep, and it is a shaping curve rather than a physical quantity: the
// ridged fbm's median sits near 0.78, and only a power this strong drags the
// BULK of the profile down to the waterline while leaving the strongest crests
// standing. Measured off the render rather than reasoned - at 2.8 the median
// silhouette line came out at 75% of frame height, still well short of the 78-80%
// the framing needs.
const RIDGE_SHARPEN = 4.5;
// The contre-jour. The land is drawn as pure black, so the crest is defined by
// letting a sliver of the sky BEHIND it through (alpha < 1 for a few pixels)
// plus a faint constant glow on top. The first term is what makes the rim
// bright exactly where the aurora behind it is bright and dim where it is not,
// which no constant line can do; the second is what keeps the crest readable
// where there is no aurora behind it at all.
const RIM_WIDTH = 0.008;
// The rim is not painted on: it is the sky BEHIND the ridge, seen through a
// crest that is held slightly transparent for a few pixels. So its brightness
// is the sky's brightness at that point, by construction - a crest under a
// bright curtain gets a sharp one, a crest under an empty patch of sky gets
// none - which is what a framebuffer sample would have given us without
// needing a render target to sample from. RIM_ALPHA is how opaque the crest
// stays: raising it from 0.32 to 0.50 cuts the sky let through by a third.
const RIM_ALPHA = 0.50;
// The constant floor, kept only so a crest over genuinely black sky does not
// vanish into it entirely. Cut from 0.16: together with the alpha above this
// is the ~40% reduction, and it is now small enough that it can no longer
// flatten the modulation into an even line.
const RIM_GLOW = 0.03;
const HAZE_HEIGHT = 0.20; // atmospheric darkening just above the waterline
const HAZE_MAX = 0.16;    // held well under 1: the rim needs sky left to show

// --- the reflection ----------------------------------------------------------
// REFLECT_SQUASH is not a cheat to fit more in. A reflection lives on a
// HORIZONTAL plane seen at a grazing angle, so it is foreshortened exactly the
// way the ground is - a real lake reflection is compressed toward the horizon,
// and a strict 1:1 mirror is the thing that looks wrong. It also happens to be
// what makes the reflection usable: mirrored 1:1 about a horizon at 81%, an
// aurora whose base sits 0.14 above the waterline reflects to 0.14 below it and
// its bright band falls off the bottom of the frame entirely.
const REFLECT_SQUASH = 2.0;
// The brief's 0.35, and it is set as a RATIO MEASURED off the render rather
// than as a shader constant taken on faith: two effects pull in opposite
// directions here and neither is worth arguing about in the abstract. The
// squash concentrates two rows of source into one row of water, which lifts
// it; dropping the fine rays removes the peaks, which lowers it. The value
// below is the one at which the mean luminance of the water measures 0.35 of
// the sky band it mirrors.
// Re-measured after the base gained its dissolve: with less bright material
// standing near the shore, and the reflection now held off REFLECT_GAP below
// it, the water had fallen to 0.24 of the sky it mirrors. This puts it back on
// the brief's 0.35.
const REFLECT_ATTEN = 0.36;
// Full strength from the waterline down to here, then out to nothing at the
// frame's bottom edge - so the last stretch of the frame holds a fading
// reflection rather than the flat black it used to be.
const REFLECT_FADE_FULL = -0.74;
// Never more than three, whatever the tier. The reflection doubles the fill
// cost of the most expensive thing in the frame, and past three curtains the
// wobble has blurred them into each other anyway.
const REFLECT_MAX_CURTAINS = 3;
const REFLECT_WOBBLE_AMP = 0.013;
// The rays are dropped in the reflection and replaced by ONE octave six times
// coarser. Fine rays would alias against the wobble, and broad soft columns is
// what a reflection of them actually looks like once the water has had its way
// with them - flat colour would read as a painted gradient instead.
// The shoreline gets its own low-frequency wobble, on a seed unrelated to the
// relief's - a lake edge does not repeat the mountain above it. Small: this is
// a shore, not a second skyline.
const SHORE_WAVE = 0.011;
const SHORE_WAVE_FREQ = 2.7;
// Where the land meets the water, a band of solid black and then a soft ramp
// out of it - about 15px at 720p all told. Darker than the water below and
// than the reflection, which is what a wet shoreline is.
const SHORE_DARK = 0.012;
const REFLECT_SHORE_SOFT = 0.030;
// The reflection does not begin at the foot of the silhouette. Real water
// leaves a dark strip in the shallows before it starts returning anything, and
// starting the mirrored curtains flush against the shore is what makes the two
// halves read as one folded picture rather than as a scene and its reflection.
const REFLECT_GAP = 0.05;
const REFLECT_RAY_FREQ = 0.16;
const REFLECT_RAY_AMP = 0.45;
// The water's own colour, under everything: the aurora's base hue at a low
// level, falling to near-black at the bottom edge. This is the tier-0
// reflection on its own, and at every tier it is what guarantees there is no
// flat black anywhere in the frame.
const WATER_TINT_LEVEL = 0.045;

const SAFE_FLOOR = 0.35;

function clamp01(x) {
  return Math.max(0, Math.min(1, x));
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

// Exponential approach, frame-rate independent. Same helper caustics.js uses.
function approach(current, target, dt, tau) {
  return current + (target - current) * (1 - Math.exp(-dt / tau));
}

function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

function capLuminance(color, maxLuma) {
  const l = LUMA.r * color.r + LUMA.g * color.g + LUMA.b * color.b;
  if (l > maxLuma && l > 0) color.multiplyScalar(maxLuma / l);
  return color;
}

// --- shared GLSL -------------------------------------------------------------

const HASH_CHUNK = `
  float hash11(float p) {
    p = fract(p * 0.1031);
    p *= p + 33.33;
    p *= p + p;
    return fract(p);
  }
  float noise11(float p) {
    float i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(hash11(i), hash11(i + 1.0), f);
  }
`;

// The safe zone, evaluated in the VERTEX stage as the shared chunk intends -
// a curtain is a dense mesh, so a per-vertex evaluation is both far cheaper
// than per-pixel and, at 128x32, fine enough that the ramp is smooth. Own
// floor (0.35) rather than the shared 0.32, per the brief; the pattern of a
// file carrying its own floor is the established one here (sumi 0.40,
// caustics its own, loom 0.55).
const SAFE_CHUNK = `
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;

  float safeZone(vec2 ndc) {
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
`;

// --- sky ---------------------------------------------------------------------

const fullscreenVertexShader = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

const skyFragmentShader = `
  precision mediump float;
  uniform vec3 uSkyTop;
  uniform vec3 uSkyHorizon;
  uniform vec3 uWaterTint;
  varying vec2 vUv;
  void main() {
    float ndcY = vUv.y * 2.0 - 1.0;
    if (ndcY < ${WATER_Y.toFixed(3)}) {
      // The lake itself. Tinted by the aurora's own base colour, because the
      // sky just above the horizon IS the aurora's base - so the water is lit
      // by what is standing over it, and it breathes with it (see uWaterTint,
      // which carries the live intensity). Falls to near black at the bottom
      // edge, which is a gradient rather than the dead flat this replaces.
      // sqrt, not a square: the lit water has to be concentrated in the band
      // just under the shoreline and gone well before the bottom edge. Squared
      // it holds its value most of the way down and the lake reads as one flat
      // slab of green - which was the original complaint, moved down the frame.
      // Falls toward a fraction of its own tint rather than to the sky's
      // black, so the bottom edge of the frame keeps a trace of colour in it.
      float t = (${WATER_Y.toFixed(3)} - ndcY) / ${(1.0 + WATER_Y).toFixed(3)};
      gl_FragColor = vec4(mix(uWaterTint, uWaterTint * 0.12, sqrt(t)), 1.0);
      return;
    }
    // Cubed, so the lift stays crushed into the last stretch above the
    // horizon instead of greying the whole upper sky.
    float t = (1.0 - vUv.y) / ${((1.0 - WATER_Y) / 2.0).toFixed(4)};
    gl_FragColor = vec4(mix(uSkyTop, uSkyHorizon, t * t * t), 1.0);
  }
`;

// --- stars -------------------------------------------------------------------
// Generated once, never touched again: position, size and brightness are all
// baked into attributes and the shader reads nothing that changes. They are
// drawn BEFORE the curtains and the curtains are additive, which is what makes
// them show THROUGH the faint ones - the brief's point about the aurora being
// translucent, and something no amount of curtain opacity tuning can fake.
const starVertexShader = `
  attribute vec4 aStar; // xy clip space, z brightness, w 1 if this is a reflection

  uniform float uPointScale;
  uniform float uPointSizeMax;
  uniform float uWaveTime;

  varying float vBright;

  void main() {
    vBright = aStar.z;
    // A reflected star sits still on moving water only if you do not look at
    // it. One slow sideways swim, on the same clock as everything else here so
    // a pause freezes it with the rest.
    float wob = aStar.w * sin(uWaveTime * 0.21 + aStar.y * 37.0) * ${REFLECT_WOBBLE_AMP.toFixed(4)};
    gl_Position = vec4(aStar.x + wob, aStar.y, 0.0, 1.0);
    gl_PointSize = min(position.z * uPointScale, uPointSizeMax);
  }
`;

const starFragmentShader = `
  precision mediump float;
  uniform vec3 uStarColor;
  varying float vBright;
  void main() {
    vec2 d = gl_PointCoord - vec2(0.5);
    float r2 = dot(d, d) * 4.0;
    if (r2 > 1.0) discard;
    float core = 1.0 - smoothstep(0.0, 1.0, r2);
    gl_FragColor = vec4(uStarColor * core * vBright, 1.0);
  }
`;

// --- horizon -----------------------------------------------------------------

const horizonFragmentShader = `
  precision mediump float;

  uniform float uAspectH;
  uniform vec3 uRimColor;
  varying vec2 vUv;

${HASH_CHUNK}

  // A skyline out of 1D noise. Ridged (1 - |2n-1|) rather than plain value
  // noise, because plain noise gives rolling hills and what is wanted is
  // peaks: the ridge transform puts a sharp crest wherever the noise crosses
  // its own midpoint, which is exactly where a mountain silhouette has one.
  // Sharpened on top of that (RIDGE_SHARPEN): raising the profile to a power
  // pushes the bulk of it down toward the waterline and leaves only the
  // strongest crests standing, which is the difference between a few frank
  // peaks and a soft swell.
  float ridgeHeight(float x) {
    float h = 0.0;
    float amp = 0.55;
    float freq = 1.0;
    for (int i = 0; i < 4; i++) {
      float n = noise11(x * freq + float(i) * 17.3);
      h += (1.0 - abs(2.0 * n - 1.0)) * amp;
      amp *= 0.55;
      freq *= 2.3;
    }
    h = pow(clamp(h, 0.0, 1.0), ${RIDGE_SHARPEN.toFixed(2)});
    return ${RIDGE_H_MIN.toFixed(3)} + h * ${(RIDGE_H_MAX - RIDGE_H_MIN).toFixed(3)};
  }

  void main() {
    float ndcY = vUv.y * 2.0 - 1.0;
    float x = (vUv.x - 0.5) * uAspectH * 2.6;
    // The waterline itself wanders. Independent of the ridge noise (its own
    // offset into the hash), so the shore does not echo the skyline.
    float water = ${WATER_Y.toFixed(3)}
      + (noise11(x * ${SHORE_WAVE_FREQ.toFixed(2)} + 131.7) - 0.5) * ${(SHORE_WAVE * 2.0).toFixed(4)};
    float crest = water + ridgeHeight(x);

    if (ndcY > crest) {
      // Above the crest: haze. Measured from the WATERLINE, not from the
      // crest - which is the whole point. Air near the horizon extinguishes
      // what is behind it as a function of how low in the sky you are looking,
      // so it is a flat horizontal band. Keyed to the crest instead (as it was
      // first written) it climbs over every peak and hangs a smooth dark dome
      // above each one, which renders as a second ghost mountain sitting
      // behind the real silhouette.
      float a = 1.0 - smoothstep(0.0, ${HAZE_HEIGHT.toFixed(2)}, ndcY - water);
      a *= a * ${HAZE_MAX.toFixed(2)};
      if (a <= 0.004) discard;
      gl_FragColor = vec4(0.0, 0.0, 0.0, a);
      return;
    }

    if (ndcY > water) {
      // The land. Pure black - anything else on this shape reads as a painted
      // backdrop rather than as ground in front of a lit sky - except along
      // the crest, where two things happen at once. The alpha is held down for
      // a few pixels so the sky BEHIND shows through, which is what makes the
      // rim bright under a bright curtain and dark where there is none; and a
      // faint constant glow is added on top so the crest stays readable even
      // over an empty patch of sky.
      float depth = crest - ndcY;
      float rim = 1.0 - smoothstep(0.0, ${RIM_WIDTH.toFixed(4)}, depth);
      float a = mix(${RIM_ALPHA.toFixed(2)}, 1.0, smoothstep(0.0, ${RIM_WIDTH.toFixed(4)}, depth));
      gl_FragColor = vec4(uRimColor * rim * ${RIM_GLOW.toFixed(2)}, a);
      return;
    }

    // Below the waterline. The land's own reflection, mirrored about the
    // shoreline and squashed with everything else in the water, and rendered
    // BLACKER than the land: no rim on it at all, and it paints over the lit
    // water underneath, so it comes out as the darkest band in the frame -
    // which is what a shoreline reflection actually is.
    float crestR = water - (crest - water) / ${REFLECT_SQUASH.toFixed(2)};
    float shoreEnd = crestR - ${SHORE_DARK.toFixed(4)};
    if (ndcY > shoreEnd - ${REFLECT_SHORE_SOFT.toFixed(4)}) {
      // The land's reflection, then the shore band: solid black for a few more
      // pixels below it and then a ramp out over a dozen more. The land's own
      // outline is sharp because it is land; its reflection lies on moving
      // water, and a razor edge there reads as a cut-out pasted on.
      float a = ndcY > shoreEnd
        ? 1.0
        : smoothstep(shoreEnd - ${REFLECT_SHORE_SOFT.toFixed(4)}, shoreEnd, ndcY);
      gl_FragColor = vec4(0.0, 0.0, 0.0, a);
      return;
    }
    // Open water: leave it to the sky quad's tint and the mirrored curtains.
    discard;
  }
`;

// --- curtains ----------------------------------------------------------------
// One InstancedBufferGeometry, one draw call. Per instance:
//   iA.x  z of the curtain's spine, world units (negative, in front of CAM_Z)
//   iA.y  seed - shifts every term of bend(), so no two curtains meander alike
//   iA.z  half width, as a fraction of the frame half-width at that depth
//   iA.w  horizontal centre offset, same units
//   iB.x  base height, world units (shared ground plane + jitter)
//   iB.y  height, world units
//   iB.z  brightness multiplier
//   iB.w  fade-in threshold: the curtain count at which this one is fully up
//   iR    1 for the mirrored copy that lives in the water, 0 for the real one
//
// The reflection is EXTRA INSTANCES of the same mesh rather than a second pass
// or a render target - the brief asks for the sky to be a function evaluated
// twice, but here the curtains are genuine geometry (that is what produces the
// fold, and no screen-space function reproduces it), so the equivalent move is
// to draw that geometry a second time mirrored. Same draw call, same shader.
function curtainVertexShader() {
  return `
  attribute vec4 iA;
  attribute vec4 iB;
  attribute float iR;

  uniform float uWaveTime;
  uniform float uTanHalf;
  uniform float uBendAmp;
  uniform float uCount;

${SAFE_CHUNK}

  varying float vU;      // position along the curtain, in world units
  varying float vH;      // 0 at the base, 1 at the top
  varying float vEdge;   // lateral falloff, 0 at the ends
  varying float vSeed;
  varying float vBright;
  varying float vRefl;

  // The brief's own curve, verbatim. Three sinusoids whose frequencies are not
  // rational multiples of each other, so the sheet never repeats: that is the
  // difference between a meander and a corrugation.
  float bend(float x, float seed) {
    return sin(x * 0.7 + uWaveTime * 0.11 + seed) * 1.9
         + sin(x * 1.6 - uWaveTime * 0.17 + seed * 2.3) * 0.8
         + sin(x * 3.1 + uWaveTime * 0.07 + seed * 4.1) * 0.35;
  }

  void main() {
    float zSpine = iA.x;
    float seed = iA.y;
    float dist0 = ${CAM_Z.toFixed(1)} - zSpine;

    // World half-width from the frame's own half-width at this depth, so a
    // curtain covers the same FRACTION of the frame whatever the aspect is.
    float halfW = iA.z * dist0 * uTanHalf * uAspect;
    float u = position.x * 2.0 * halfW;
    float centre = iA.w * dist0 * uTanHalf * uAspect;

    // The meander, and with it the whole point of the piece: z now varies
    // along the curtain, so the projection below divides by a DIFFERENT
    // distance at every point of it. Where the sheet turns edge-on, screen x
    // stops advancing and the mesh folds back over itself.
    // The meander is TAPERED OFF toward the curtain's two ends, and that is
    // what keeps the lateral falloff from arriving as a hard vertical seam.
    // The falloff is written in the sheet's own parameter, but a fold near an
    // end compresses that parameter into a handful of pixels, so a ramp a
    // quarter of the sheet wide can still land as a step - which is what put
    // straight vertical edges through the frame where the brighter curtains
    // finished. Flat ends do not fold, so the ramp arrives at its full width.
    // It is also what a curtain does: the meander dies out where the sheet does.
    float taper = 1.0 - 0.72 * smoothstep(0.28, 0.5, abs(position.x));
    float z = zSpine + bend(u, seed) * uBendAmp * taper;
    float dist = max(${CAM_Z.toFixed(1)} - z, 0.6);

    // The base rides the same curve as the fold (own seed), so where the sheet
    // turns, its foot rises and falls with it.
    float baseY = iB.x + bend(u, seed * 3.7) * ${BASE_WAVE.toFixed(2)};
    float worldY = baseY + position.y * iB.y;

    vec2 ndc = vec2(
      (u + centre) / (dist * uTanHalf * uAspect),
      worldY / (dist * uTanHalf)
    );

    vU = u;
    vH = position.y;
    // A WIDE ramp - a quarter of the curtain at each end - and that width is
    // the point. A narrow falloff (the first version faded over a tenth) does
    // not survive the fold: near the ends of the sheet the projection is most
    // compressed, so the last tenth of the PARAMETER can land inside thirty
    // pixels of screen and the attenuation arrives as the hard vertical edge
    // the brief specifically rules out. Worse, with several curtains up, one
    // sheet's abrupt end sitting across another's base is what turned the
    // horizon into a row of flat-topped rectangles.
    // Wider still in the water. The reflection is squashed vertically, which
    // compresses its lateral ends harder than the sky copy's, and a mirrored
    // curtain ending on a straight edge is the one thing that says "second
    // copy of the geometry" out loud. A ramp across two fifths of the sheet is
    // most of what "floutee horizontalement" buys here.
    vEdge = smoothstep(0.5, iR > 0.5 ? 0.10 : 0.26, abs(position.x));
    vSeed = seed;
    // Fade-in over the last curtain-worth of the count, so progress adding a
    // curtain is a curtain BRIGHTENING rather than one appearing.
    float appear = smoothstep(iB.w - ${CURTAIN_FADE.toFixed(2)}, iB.w, uCount);
    vBright = iB.z * appear * safeZone(ndc);
    vRefl = iR;

    if (iR > 0.5) {
      // Mirror about the shoreline, foreshortened (see REFLECT_SQUASH), and
      // set swimming by two slow sines of the DEPTH below the waterline -
      // which is what makes the columns wander the way a reflection does
      // rather than the way the thing being reflected does.
      float depth = ndc.y - ${WATER_Y.toFixed(3)};
      ndc.y = ${WATER_Y.toFixed(3)} - depth / ${REFLECT_SQUASH.toFixed(2)};
      ndc.x += (sin(ndc.y * 14.0 + uWaveTime * 0.13) * 0.62
              + sin(ndc.y * 31.0 - uWaveTime * 0.09) * 0.38) * ${REFLECT_WOBBLE_AMP.toFixed(4)};
      // Full under the shoreline, gone by the bottom edge; and nothing at all
      // above the waterline, where a fold could otherwise throw part of a
      // mirrored curtain back up into the sky.
      float fade = smoothstep(-1.0, ${REFLECT_FADE_FULL.toFixed(2)}, ndc.y);
      // Held off REFLECT_GAP below the waterline, so the shallows stay dark
      // and the reflection starts a little way out.
      fade *= 1.0 - smoothstep(${(WATER_Y - REFLECT_GAP - 0.03).toFixed(4)}, ${(WATER_Y - REFLECT_GAP).toFixed(4)}, ndc.y);
      vBright *= ${REFLECT_ATTEN.toFixed(2)} * fade;
    }

    gl_Position = vec4(ndc, 0.0, 1.0);
  }
`;
}

function curtainFragmentShader(withStriae, withAltitude) {
  return `
  precision mediump float;

  uniform float uStriaTime;
  uniform float uStriaAmp;
  uniform float uStriaFreq;
  uniform float uGain;
  uniform vec3 uLowColor;
  uniform vec3 uHighColor;

  varying float vU;
  varying float vH;
  varying float vEdge;
  varying float vSeed;
  varying float vBright;
  varying float vRefl;

${HASH_CHUNK}

  void main() {
    if (vBright <= 0.002) discard;

    // --- the rays ----------------------------------------------------------
    // A function of the curtain's OWN horizontal coordinate, never of screen
    // x. That is what makes them ride the sheet: through a fold they compress
    // with the geometry instead of sliding across it, which is the single
    // detail that says "structure on a surface" rather than "stripes on a
    // filter". Two octaves, the second nearly three times finer, and squared -
    // real rays are thin bright lines with dark between them, not a ripple.
    float rays = 1.0;
    ${withStriae ? `
    if (vRefl > 0.5) {
      // In the water: ONE octave, six times coarser, at reduced amplitude.
      // Fine rays here would alias against the wobble and cost a second noise
      // for the privilege; broad soft columns are what a reflection of them
      // looks like anyway once the surface has blurred them together.
      float sr = vU * uStriaFreq * ${REFLECT_RAY_FREQ.toFixed(3)} + uStriaTime * 0.4 + vSeed * 31.0;
      rays = mix(1.0 - ${REFLECT_RAY_AMP.toFixed(2)} * 0.6, 1.0 + ${REFLECT_RAY_AMP.toFixed(2)}, noise11(sr));
    } else {
      float s = vU * uStriaFreq + uStriaTime + vSeed * 31.0;
      float n = noise11(s) * 0.62 + noise11(s * 2.73 + 11.0) * 0.38;
      n = n * n;
      rays = mix(1.0 - uStriaAmp * 0.75, 1.0 + uStriaAmp * 1.35, n);
    }
    ` : ''}

    // --- the vertical gradient ---------------------------------------------
    // Sharp at the base, exponential upward, and the decay LENGTH itself
    // varies slowly along the curtain - without that the top edge dissolves
    // on one horizontal line and the whole thing reads as a rectangle with a
    // gradient in it.
    //
    // The base ramp is 0.07 of the height, and it was 0.03 first. Too short is
    // a real defect rather than a taste one: a curtain's base is a hard cut in
    // the mesh, the fold moves that cut up and down the frame as the sheet
    // turns, and five of them at 0.03 rendered as a staircase of flat-bottomed
    // rectangular blocks along the horizon. At 0.07 the cut is still three to
    // seven times sharper than the tail above it (decay runs 0.20 to 0.52), so
    // the asymmetry the brief asks for is fully intact - it just no longer
    // ends on a straight line.
    float decay = mix(0.20, 0.52, noise11(vU * 0.33 + vSeed * 7.0));
    // The top fade is not cosmetic. The mesh ENDS at vH = 1, and with the long
    // decay lengths a curtain still carries about 15% of its base brightness
    // when it gets there - so without this the tail is cut off by a hard
    // horizontal line wherever a curtain's top falls inside the frame, which
    // is the exact opposite of "le haut se dissipe progressivement".
    float grad = exp(-vH / decay)
      * smoothstep(0.0, ${BASE_DISSOLVE.toFixed(2)}, vH)
      * (1.0 - smoothstep(0.80, 1.0, vH));

    float intensity = grad * rays * vEdge * vBright * uGain;
    if (intensity <= 0.002) discard;

    // --- colour by altitude -------------------------------------------------
    // Green low, violet-red high: different emission lines at different
    // altitudes, and the most recognisable of the three markers. The changeover
    // is deliberately LOW - it begins an eighth of the way up and is complete
    // by three fifths. Placed any higher (the first version started at 0.22 and
    // ran to 0.86) it lands where exp(-vH/decay) has already taken the curtain
    // down past a fifth of its brightness, and the marker the whole effect is
    // recognised by ends up too faint to read at all.
    vec3 col = ${withAltitude ? 'mix(uLowColor, uHighColor, smoothstep(0.12, 0.60, vH))' : 'uLowColor'};

    gl_FragColor = vec4(col * intensity, 1.0);
  }
`;
}

export class AuroraVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;

    // ?curtains=<n> - pins the count, so the fold behaviour of a single sheet
    // can be looked at without four others summed on top of it.
    const forced = parseInt(urlParam('curtains'), 10);
    this._forcedCount = Number.isFinite(forced) && forced >= 1 && forced <= 5 ? forced : null;
    // ?nohorizon=1 - drops the silhouette, for checking how far down the
    // curtain bases actually reach.
    this._noHorizon = urlParam('nohorizon') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const layout = TIER[this._tier];
    this._curtainCount = this._forcedCount != null ? this._forcedCount : layout.curtains;

    const tanHalf = Math.tan(THREE.MathUtils.degToRad(FOV_DEG) / 2);

    // --- sky --------------------------------------------------------------
    this._skyUniforms = {
      uSkyTop: { value: new THREE.Color(SKY_TOP) },
      uSkyHorizon: { value: new THREE.Color(SKY_HORIZON) },
      uWaterTint: { value: new THREE.Color(FALLBACK_LOW) }
    };
    this._waterGain = layout.waterGain;
    this._skyGeometry = new THREE.PlaneGeometry(1, 1);
    this._skyMaterial = new THREE.ShaderMaterial({
      uniforms: this._skyUniforms,
      vertexShader: fullscreenVertexShader,
      fragmentShader: skyFragmentShader,
      transparent: false,
      depthTest: false,
      depthWrite: false
    });
    this._sky = new THREE.Mesh(this._skyGeometry, this._skyMaterial);
    this._sky.frustumCulled = false;
    this._sky.renderOrder = -1000;
    scene.add(this._sky);

    this._buildStars(scene, layout.stars);
    this._buildCurtains(scene, layout, tanHalf);

    // --- horizon ----------------------------------------------------------
    // Drawn LAST and opaque, so it occludes the bottom of every curtain. An
    // aurora is behind the mountains, not in front of them, and the black
    // shape cutting into the lit base is most of what gives the frame scale.
    if (!this._noHorizon) {
      this._horizonUniforms = {
        uAspectH: { value: 1.78 },
        uRimColor: { value: new THREE.Color(FALLBACK_LOW) }
      };
      this._horizonGeometry = new THREE.PlaneGeometry(1, 1);
      this._horizonMaterial = new THREE.ShaderMaterial({
        uniforms: this._horizonUniforms,
        vertexShader: fullscreenVertexShader,
        fragmentShader: horizonFragmentShader,
        transparent: true,
        blending: THREE.NormalBlending,
        depthTest: false,
        depthWrite: false
      });
      this._horizon = new THREE.Mesh(this._horizonGeometry, this._horizonMaterial);
      this._horizon.frustumCulled = false;
      this._horizon.renderOrder = -700;
      scene.add(this._horizon);
    }

    // --- state ------------------------------------------------------------
    this._time = 0;
    this._waveTime = 0;
    this._striaTime = 0;
    this._gain = 1;
    this._waveRate = 1;
    this._striaAmp = (HIGH_STRIA_MIN + HIGH_STRIA_MAX) * 0.5;
    this._striaRate = 1;
    this._count = COUNT_MIN;

    this._lastPaletteRef = undefined;
    this._lowFrom = new THREE.Color(FALLBACK_LOW);
    this._lowTo = new THREE.Color(FALLBACK_LOW);
    this._highFrom = new THREE.Color(FALLBACK_HIGH);
    this._highTo = new THREE.Color(FALLBACK_HIGH);
    this._paletteLerp = 1;
    this._scratch = new THREE.Color();
    this._applyPalette(null);
    this._lowFrom.copy(this._lowTo);
    this._highFrom.copy(this._highTo);
    this._writeColors();
  }

  _buildStars(scene, count) {
    // A share of them again, mirrored into the lake. Positions are computed
    // here once and never touched: a reflected star is the cheapest thing in
    // this file, and leaving them out is what makes water look like a hole.
    const reflected = Math.round(count * STAR_REFLECT_FRACTION);
    const total = count + reflected;
    const stars = new Float32Array(total * 4);
    // `position` is never read as geometry - the vertex shader builds clip
    // space from aStar - but Three.js needs it to know the draw count, and
    // its z carries the point size so no extra attribute is needed.
    const positions = new Float32Array(total * 3);
    for (let i = 0; i < count; i++) {
      stars[i * 4] = Math.random() * 2 - 1;
      // Nothing behind the peaks: it would be covered by the silhouette
      // anyway, and the ones that survived at its edge would read as fireflies.
      stars[i * 4 + 1] = STAR_FLOOR_NDC + Math.random() * (1 - STAR_FLOOR_NDC);
      // Cubed, so most stars are faint and a few are not. A uniform draw gives
      // a flat field of identical dots, which no sky has ever looked like.
      const b = Math.random();
      stars[i * 4 + 2] = 0.18 + 0.82 * b * b * b;
      stars[i * 4 + 3] = 0;
      positions[i * 3 + 2] = STAR_SIZE_MIN + Math.random() * (STAR_SIZE_MAX - STAR_SIZE_MIN);
    }
    for (let k = 0; k < reflected; k++) {
      const src = Math.floor(Math.random() * count);
      const i = count + k;
      // Mirrored and foreshortened on the same terms as everything else in the
      // water, so a star and a curtain agree about where the surface is.
      const y = WATER_Y - (stars[src * 4 + 1] - WATER_Y) / REFLECT_SQUASH;
      stars[i * 4] = stars[src * 4];
      stars[i * 4 + 1] = y;
      stars[i * 4 + 2] = stars[src * 4 + 2] * STAR_REFLECT_LEVEL
        * Math.max(0, Math.min(1, (y + 1) / (1 + REFLECT_FADE_FULL)))
        * (y > WATER_Y - REFLECT_GAP ? 0 : 1);
      stars[i * 4 + 3] = 1;
      positions[i * 3 + 2] = positions[src * 3 + 2];
    }
    this._starGeometry = new THREE.BufferGeometry();
    this._starGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this._starGeometry.setAttribute('aStar', new THREE.BufferAttribute(stars, 4));
    this._starGeometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);

    this._starUniforms = {
      uWaveTime: { value: 0 },
      uPointScale: { value: 1 },
      uPointSizeMax: { value: Math.max(1, getMaxPointSize()) },
      uStarColor: { value: new THREE.Color(0xcfe0ff) }
    };
    this._starMaterial = new THREE.ShaderMaterial({
      uniforms: this._starUniforms,
      vertexShader: starVertexShader,
      fragmentShader: starFragmentShader,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthTest: false,
      depthWrite: false
    });
    this._stars = new THREE.Points(this._starGeometry, this._starMaterial);
    this._stars.frustumCulled = false;
    this._stars.renderOrder = -900;
    scene.add(this._stars);
  }

  _buildCurtains(scene, layout, tanHalf) {
    const n = this._curtainCount;
    // The NEAREST few get mirrored, not the far ones. A far curtain's base
    // rides high in the frame, so its reflection lands deep in the fade and
    // most of it falls off the bottom edge; the near ones sit just over the
    // shoreline and their bright bases are what actually fills the water.
    const nRef = Math.min(REFLECT_MAX_CURTAINS, layout.reflect, n);
    this._reflectCount = nRef;
    const plane = new THREE.PlaneGeometry(1, 1, layout.segX, layout.segY);
    // PlaneGeometry is centred on the origin; the shader wants y running 0 at
    // the base to 1 at the top, so it is shifted once here rather than with a
    // +0.5 on every one of 4257 vertices per instance, every frame.
    plane.translate(0, 0.5, 0);

    const geometry = new THREE.InstancedBufferGeometry();
    geometry.setAttribute('position', plane.getAttribute('position'));
    geometry.setIndex(plane.getIndex());
    plane.dispose();

    const total = n + nRef;
    const a = new Float32Array(total * 4);
    const b = new Float32Array(total * 4);
    const r = new Float32Array(total);
    for (let i = 0; i < n; i++) {
      const f = n > 1 ? i / (n - 1) : 0;
      a[i * 4 + 0] = lerp(Z_NEAR, Z_FAR, f);
      a[i * 4 + 1] = Math.random() * 12;
      a[i * 4 + 2] = lerp(HALF_WIDTH_MIN, HALF_WIDTH_MAX, Math.random());
      a[i * 4 + 3] = (Math.random() * 2 - 1) * CENTRE_OFFSET;
      b[i * 4 + 0] = BASE_WORLD_Y + (Math.random() * 2 - 1) * BASE_JITTER;
      b[i * 4 + 1] = lerp(HEIGHT_WORLD_MIN, HEIGHT_WORLD_MAX, Math.random());
      b[i * 4 + 2] = lerp(BRIGHT_MIN, BRIGHT_MAX, Math.random());
      // The far curtains are the ones already up at the start of a track and
      // the near ones arrive as it builds - an aurora that grows toward you
      // reads as intensifying, one that recedes reads as ending.
      b[i * 4 + 3] = n - i;
      r[i] = 0;
    }
    for (let k = 0; k < nRef; k++) {
      const i = n + k;
      a.set(a.subarray(k * 4, k * 4 + 4), i * 4);
      b.set(b.subarray(k * 4, k * 4 + 4), i * 4);
      r[i] = 1;
    }
    geometry.setAttribute('iA', new THREE.InstancedBufferAttribute(a, 4));
    geometry.setAttribute('iB', new THREE.InstancedBufferAttribute(b, 4));
    geometry.setAttribute('iR', new THREE.InstancedBufferAttribute(r, 1));
    geometry.instanceCount = total;
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
    this._curtainGeometry = geometry;

    this._curtainUniforms = Object.assign({}, safeZoneUniforms, {
      uWaveTime: { value: 0 },
      uTanHalf: { value: tanHalf },
      uBendAmp: { value: 1 },
      uCount: { value: COUNT_MIN },
      uStriaTime: { value: 0 },
      uStriaAmp: { value: (HIGH_STRIA_MIN + HIGH_STRIA_MAX) * 0.5 },
      uStriaFreq: { value: 4.2 },
      uGain: { value: 1 },
      uLowColor: { value: new THREE.Color(FALLBACK_LOW) },
      uHighColor: { value: new THREE.Color(FALLBACK_HIGH) }
    });

    this._curtainMaterial = new THREE.ShaderMaterial({
      uniforms: this._curtainUniforms,
      vertexShader: curtainVertexShader(),
      // Striae and the altitude gradient are on at every tier: they are two of
      // the three things that make this read as an aurora at all, so the tier
      // dial is curtain COUNT and mesh density, never these.
      fragmentShader: curtainFragmentShader(true, true),
      transparent: true,
      blending: THREE.AdditiveBlending,
      // Between the curtains themselves as well as against the sky. Additive
      // with a depth test would let whichever curtain drew first cut a hard
      // silhouette out of the ones behind it - the exact vertical seams the
      // brief warns about.
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide
    });
    this._curtains = new THREE.Mesh(this._curtainGeometry, this._curtainMaterial);
    this._curtains.frustumCulled = false;
    this._curtains.renderOrder = -800;
    scene.add(this._curtains);
  }

  // Two colours off the cover, pushed toward the two emission lines. The pair
  // chosen is the one FURTHEST APART in hue, not the first two in the list:
  // the whole altitude gradient is the difference between them, and two
  // neighbouring shades of the same blue would collapse it.
  _applyPalette(palette) {
    const vivid = palette && palette.vividColors;
    if (!vivid || vivid.length < 2) {
      this._lowTo.set(FALLBACK_LOW);
      this._highTo.set(FALLBACK_HIGH);
      capLuminance(this._lowTo, LUMA_CAP);
      capLuminance(this._highTo, LUMA_CAP);
      return;
    }
    let bestA = 0, bestB = 1, bestD = -1;
    for (let i = 0; i < vivid.length; i++) {
      for (let j = i + 1; j < vivid.length; j++) {
        const d = hueDistance(vivid[i], vivid[j]);
        if (d > bestD) { bestD = d; bestA = i; bestB = j; }
      }
    }
    // Whichever of the pair is cooler takes the base (green sits at the cool
    // end), the warmer one goes aloft - so the pull toward each anchor is a
    // nudge rather than an override wherever the cover already agrees.
    const wa = warmth(vivid[bestA]);
    const wb = warmth(vivid[bestB]);
    const lowHex = wa <= wb ? vivid[bestA] : vivid[bestB];
    const highHex = wa <= wb ? vivid[bestB] : vivid[bestA];

    this._lowTo.set(lowHex).lerp(this._scratch.set(GREEN_ANCHOR), GREEN_PULL);
    this._highTo.set(highHex).lerp(this._scratch.set(HIGH_ANCHOR), HIGH_PULL);
    capLuminance(this._lowTo, LUMA_CAP);
    capLuminance(this._highTo, LUMA_CAP);
  }

  _writeColors() {
    const u = this._curtainUniforms;
    u.uLowColor.value.copy(this._lowFrom).lerp(this._lowTo, this._paletteLerp);
    u.uHighColor.value.copy(this._highFrom).lerp(this._highTo, this._paletteLerp);
    // The crest's own glow is the aurora's base colour - it is the light that
    // is actually behind the ridge - kept dim so the alpha notch stays the
    // thing that decides how bright the rim reads.
    if (this._horizonUniforms) {
      this._horizonUniforms.uRimColor.value.copy(u.uLowColor.value);
    }
  }

  _maybeAdoptPalette(profile) {
    if (profile.artworkPalette === this._lastPaletteRef) return;
    this._lastPaletteRef = profile.artworkPalette;
    this._lowFrom.copy(this._curtainUniforms.uLowColor.value);
    this._highFrom.copy(this._curtainUniforms.uHighColor.value);
    this._applyPalette(profile.artworkPalette);
    this._paletteLerp = 0;
  }

  update(deltaTime, profile) {
    // "pause -> gel complet." Every clock in this file is integrated from
    // deltaTime, so returning here freezes the meander, the ray drift and the
    // palette crossfade together, and nothing has to remember to hold.
    if (profile.playbackState !== 'PLAYING') return;

    this._time += deltaTime;
    this._maybeAdoptPalette(profile);

    const director = profile.director;
    const bands = director && director.bands;
    const character = director && director.character;
    // hasReliableGrid is exactly "the bands are real" - the same flag
    // halftone.js switches its own fallback on.
    const live = !!(director && director.hasReliableGrid && bands);
    const dynamics = character && character.dynamics != null ? character.dynamics : 0.5;
    const amp = lerp(DYNAMICS_AMP_MIN, DYNAMICS_AMP_MAX, clamp01(dynamics));

    // "Sans analyse: LFO lents." Two slow oscillators standing in for the
    // bands. The brief is right that the result is nearly identical - almost
    // nothing here is keyed to an event, so there is no rhythm to lose.
    const t = this._time;
    const low = live ? clamp01(bands.low) : 0.5 + 0.5 * Math.sin(2 * Math.PI * t / LFO_INTENSITY_S);
    const mid = live ? clamp01(bands.mid) : 0.5 + 0.5 * Math.sin(2 * Math.PI * t / LFO_SPEED_S + 1.9);
    const high = live ? clamp01(bands.high)
      : 0.5 + 0.5 * Math.sin(2 * Math.PI * t / (LFO_SPEED_S * 0.61) + 4.2);

    // bands.low -> overall intensity, +/-20%.
    this._gain = approach(this._gain, 1 + (low - 0.5) * 2 * LOW_SWING * amp, deltaTime, TAU_INTENSITY);
    // bands.mid -> undulation speed, +/-25%. Integrated into its own clock
    // rather than multiplying uTime, so a change of speed never teleports the
    // curtains: the phase is continuous through it.
    this._waveRate = approach(this._waveRate, 1 + (mid - 0.5) * 2 * MID_SWING * amp, deltaTime, TAU_WAVE);
    this._waveTime += deltaTime * this._waveRate;
    // bands.high -> the rays. The most visible reaction of the three, and the
    // truest: it is the fine structure that shivers when the highs come up.
    this._striaAmp = approach(this._striaAmp,
      lerp(HIGH_STRIA_MIN, HIGH_STRIA_MAX, high) * amp, deltaTime, TAU_STRIA);
    this._striaRate = approach(this._striaRate,
      lerp(HIGH_DRIFT_MIN, HIGH_DRIFT_MAX, high), deltaTime, TAU_STRIA);
    this._striaTime += deltaTime * this._striaRate * 0.09;

    // progress -> how many curtains stand. Two at either end of a track, all
    // of them across the middle.
    const progress = profile.duration > 0 ? clamp01(profile.progress) : 0.5;
    const target = this._forcedCount != null
      ? this._forcedCount
      : COUNT_MIN + (this._curtainCount - COUNT_MIN) * Math.sin(Math.PI * progress);
    this._count = approach(this._count, target, deltaTime, 1.5);

    const u = this._curtainUniforms;
    // The lake is lit by what stands over it, so its tint carries the live
    // intensity as well as the colour - the water brightens and dims with the
    // aurora instead of sitting there as a painted-on gradient.
    this._skyUniforms.uWaterTint.value
      .copy(u.uLowColor.value)
      .multiplyScalar(WATER_TINT_LEVEL * this._waterGain * this._gain);
    this._starUniforms.uWaveTime.value = this._waveTime;
    u.uWaveTime.value = this._waveTime;
    u.uStriaTime.value = this._striaTime;
    u.uStriaAmp.value = this._striaAmp;
    // See CURTAIN_GAIN. The divisor tracks the LIVE count, so progress adding
    // a curtain brightens the frame a little rather than a fifth more.
    u.uGain.value = CURTAIN_GAIN * this._gain / (1 + OVERLAP_K * Math.max(0, this._count - 1));
    u.uCount.value = this._count;

    if (this._paletteLerp < 1) {
      this._paletteLerp = Math.min(1, this._paletteLerp + deltaTime * 1000 / PALETTE_LERP_MS);
      this._writeColors();
    }

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const pixelHeight = window.innerHeight * dpr;
    this._starUniforms.uPointScale.value = Math.max(0.5, pixelHeight / 1080);
    if (this._horizonUniforms) {
      this._horizonUniforms.uAspectH.value = safeZoneUniforms.uAspect.value;
    }
  }

  dispose() {
    this._scene.remove(this._sky);
    this._skyGeometry.dispose();
    this._skyMaterial.dispose();
    this._scene.remove(this._stars);
    this._starGeometry.dispose();
    this._starMaterial.dispose();
    this._scene.remove(this._curtains);
    this._curtainGeometry.dispose();
    this._curtainMaterial.dispose();
    if (this._horizon) {
      this._scene.remove(this._horizon);
      this._horizonGeometry.dispose();
      this._horizonMaterial.dispose();
    }
  }
}

// --- palette helpers ---------------------------------------------------------

function hueOf(hex) {
  const r = ((hex >> 16) & 255) / 255, g = ((hex >> 8) & 255) / 255, b = (hex & 255) / 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
  if (mx === mn) return 0;
  const d = mx - mn;
  if (mx === r) return (((g - b) / d + (g < b ? 6 : 0)) / 6);
  if (mx === g) return (((b - r) / d + 2) / 6);
  return (((r - g) / d + 4) / 6);
}

function hueDistance(a, b) {
  const d = Math.abs(hueOf(a) - hueOf(b));
  return Math.min(d, 1 - d);
}

function warmth(hex) {
  return ((hex >> 16) & 255) - (hex & 255);
}
